import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { Plan } from '@processpuzzle/platform-admin';
import { catchError, EMPTY, tap } from 'rxjs';
import { KeyAvailability, PlanCatalogService } from './plan-catalog.service';
import { isDraftComplete, Signup, SignupDraft, SIGNUP_STEPS, SignupStep } from './signup';
import { SignupDraftStorage } from './signup-draft.storage';
import { SignupMapper } from './signup.mapper';
import { SignupService } from './signup.service';
import { StarterCatalogService, StarterOption } from './starter-catalog.service';
import { RememberedOrgKeyStorage } from './remembered-org-key';

interface SignupState {
  plans: Plan[];
  plansLoaded: boolean;
  starters: StarterOption[];
  startersLoaded: boolean;
  draft: SignupDraft;
  keyAvailability: KeyAvailability | undefined;
  checkingKey: boolean;
  submitting: boolean;
  submitted: Signup | undefined;
}

const initialState: SignupState = {
  plans: [],
  plansLoaded: false,
  starters: [],
  startersLoaded: false,
  draft: {},
  keyAvailability: undefined,
  checkingKey: false,
  submitting: false,
  submitted: undefined,
};

/**
 * The funnel's whole state: the catalogue, the draft being filled in, the live key check, and the
 * submission.
 *
 * **`providedIn: 'root'`, not route-scoped.** The draft has to survive the submit's authentication
 * redirect, which unloads the document entirely — so the store's lifetime is not what preserves it
 * and a route-scoped instance would buy nothing. {@link SignupDraftStorage} is what preserves it;
 * this store reads it back on construction.
 *
 * **The draft is mirrored to storage on every patch**, not on step change. The plan said step
 * change, and per-patch is strictly safer for the same cost: `authenticate()` can be reached from
 * the consent step without a further step transition, and a field edited after the last transition
 * would otherwise be the one field lost.
 *
 * **Failures are not held in state.** `centralHttpErrorInterceptor` already surfaces them, so an
 * `error` field here would mean two things showing the customer the same problem. What is held is
 * `submitting` and `checkingKey`, because those drive a disabled button and a spinner and no
 * interceptor can know about either.
 */
export const SignupStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    /** The plan the customer picked, resolved against the catalogue. */
    selectedPlan: computed(() => store.plans().find((plan) => plan.code === store.draft().subscription?.planCode)),

    /**
     * Whether the draft can be submitted at all.
     *
     * Deliberately not "is every step's form valid": the forms own their own field-level rules and
     * are the only things that can report them to the customer. This is the coarser question the
     * submit button needs, and it also catches a draft restored from an older visit that is missing
     * a step the funnel has since grown — a case no form can see, because it was never rendered.
     */
    canSubmit: computed(() => isDraftComplete(store.draft()) && !store.submitting()),

    /** True only once the check has returned and said no. Absent is "not checked yet", not "taken". */
    keyRejected: computed(() => store.keyAvailability()?.available === false),
  })),
  withMethods((store) => {
    const plansService = inject(PlanCatalogService);
    const starterCatalog = inject(StarterCatalogService);
    const signupService = inject(SignupService);
    const mapper = inject(SignupMapper);
    const draftStorage = inject(SignupDraftStorage);
    const rememberedOrgKey = inject(RememberedOrgKeyStorage);

    /** One place writes the draft, so no path can update state without mirroring it to storage. */
    function patchDraft(draft: SignupDraft): void {
      patchState(store, { draft });
      draftStorage.write(draft);
    }

    return {
      /**
       * Loads the catalogue once.
       *
       * `plansLoaded` rather than `plans.length > 0`, because an empty catalogue is a legitimate —
       * if alarming — answer, and retrying it on every step change would hammer the endpoint for a
       * list that is still going to be empty.
       */
      loadPlans(): void {
        if (store.plansLoaded()) return;
        plansService
          .findAll()
          .pipe(
            tap((plans) => patchState(store, { plans, plansLoaded: true })),
            // The interceptor has already told the customer. Marking it loaded anyway would hide a
            // real failure behind an empty list, so the flag stays false and the next step change
            // tries again.
            catchError(() => EMPTY),
          )
          .subscribe();
      },

      /**
       * Loads the Business Starter catalog once, the same way {@link loadPlans} loads the plans.
       *
       * A failure leaves the list empty, which the Plan step renders as the empty application alone:
       * the customer can still sign up, and the interceptor has already reported the failure.
       */
      loadStarters(): void {
        if (store.startersLoaded()) return;
        starterCatalog
          .findAll()
          .pipe(
            tap((starters) => patchState(store, { starters, startersLoaded: true })),
            catchError(() => EMPTY),
          )
          .subscribe();
      },

      /** Merges one step's fields into the draft. Shallow by section, which is how the steps write. */
      updateDraft(patch: SignupDraft): void {
        patchDraft({ ...store.draft(), ...patch });
      },

      /**
       * Restores a draft left behind by an authentication redirect.
       *
       * Called by the funnel component on construction. It does not overwrite a draft already in
       * state: the store is root-provided, so a customer navigating back into the funnel within one
       * page load has the newer version in memory and the stored one is a strictly older copy.
       */
      restoreDraft(): void {
        if (Object.keys(store.draft()).length > 0) return;
        const stored = draftStorage.read();
        if (stored) patchState(store, { draft: stored });
      },

      /**
       * Asks whether a key can be claimed. Debouncing belongs to the caller, not here — the
       * component owns the input event and can cancel on its own destruction.
       */
      checkKey(key: string): void {
        if (!key) {
          patchState(store, { keyAvailability: undefined, checkingKey: false });
          return;
        }
        patchState(store, { checkingKey: true });
        plansService
          .checkKey(key)
          .pipe(
            tap((keyAvailability) => patchState(store, { keyAvailability, checkingKey: false })),
            catchError(() => {
              // Unknown, not unavailable. Reporting "taken" because a request failed would send the
              // customer away from a key that is theirs to claim.
              patchState(store, { keyAvailability: undefined, checkingKey: false });
              return EMPTY;
            }),
          )
          .subscribe();
      },

      /**
       * Submits the draft.
       *
       * Clears the draft from storage and remembers the organization key, in that order, only once
       * the gateway has accepted. A draft cleared optimistically would be gone after a 409, and the
       * customer would have to fill in five steps again to change one field.
       *
       * @returns nothing; the caller watches `submitted()` and navigates on it
       */
      submit(): void {
        if (!store.canSubmit()) return;
        patchState(store, { submitting: true });
        signupService
          .submit(mapper.toRequest(store.draft()))
          .pipe(
            tap((submitted) => {
              patchState(store, { submitted, submitting: false });
              draftStorage.clear();
              rememberedOrgKey.write(submitted.orgKey);
            }),
            catchError(() => {
              // The interceptor reported it. The draft stays exactly as it was, which is what lets
              // the customer correct one field and submit again.
              patchState(store, { submitting: false });
              return EMPTY;
            }),
          )
          .subscribe();
      },

      /** Forgets everything, for a customer who starts a second sign-up in the same tab. */
      reset(): void {
        patchState(store, { ...initialState, plans: store.plans(), plansLoaded: store.plansLoaded(), starters: store.starters(), startersLoaded: store.startersLoaded() });
        draftStorage.clear();
      },

      /**
       * Whether a step has everything it needs, for the stepper's `completed` binding.
       *
       * Section-level rather than field-level, for the reason {@link SignupStore} gives about
       * `canSubmit`: the forms report their own field errors, and this answers only whether the
       * customer has been through the step.
       */
      isStepComplete(step: SignupStep): boolean {
        const draft = store.draft();
        switch (step) {
          case 'plan':
            return !!draft.subscription?.planCode && !!draft.subscription?.quantity;
          case 'organization':
            return !!draft.organization?.key && !!draft.organization?.name && !!draft.organization?.defaultLocale;
          case 'billingParty':
            return !!draft.billingParty?.legalName && !!draft.billingParty?.legalForm && !!draft.billingParty?.billingEmail && !!draft.billingParty?.registeredAddress?.country;
          case 'administrator':
            return !!draft.administrator?.email && !!draft.administrator?.firstName && !!draft.administrator?.lastName;
          case 'consent':
            return !!draft.consent?.termsOfServiceVersion && !!draft.consent?.dataProcessingAgreementVersion;
          default:
            return false;
        }
      },

      /** The steps, in order, for a template that should not import the constant itself. */
      steps(): readonly SignupStep[] {
        return SIGNUP_STEPS;
      },
    };
  }),
  withDevtools('Signup'),
);
